<script setup lang="ts">
type QuickFactIcon = "clock" | "calendar" | "age" | "ticket";
type HighlightIcon = "slide" | "splash" | "lifeguard" | "wristband";

interface PriceRow {
  label: string;
  low_season: string;
  high_season: string;
  low_standard?: string;
  high_standard?: string;
  highlight?: boolean;
}

interface QuickFact {
  icon: QuickFactIcon;
  label: string;
  value: string;
}

interface Highlight {
  icon: HighlightIcon;
  label: string;
  note?: string;
}

interface AquaparkBlock {
  header: string;
  intro?: string;
  compare_intro?: string;
  note?: string;
  image: string;
  image_alt?: string;
  secondary_image?: string;
  secondary_image_alt?: string;
  quick_facts?: QuickFact[];
  highlights?: Highlight[];
  price_low_label: string;
  price_high_label: string;
  price_rows: PriceRow[];
}

defineProps<{
  block: AquaparkBlock;
}>();

const { t } = useI18n();
const localePath = useLocalePath();
</script>

<template>
  <section
    class="relative overflow-hidden bg-gradient-to-b from-white via-orange-50/30 to-white dark:from-slate-900 dark:via-slate-900 dark:to-slate-900"
  >
    <div
      aria-hidden="true"
      class="pointer-events-none absolute -top-32 -right-16 h-80 w-80 rounded-full bg-orange-200/30 blur-3xl dark:bg-orange-500/10"
    />
    <div
      aria-hidden="true"
      class="pointer-events-none absolute -bottom-32 left-1/2 h-80 w-80 -translate-x-1/2 rounded-full bg-amber-200/30 blur-3xl dark:bg-amber-500/10"
    />

    <div data-reveal class="relative mx-auto w-full max-w-7xl px-4 py-8 md:py-12">
      <header class="mb-6 md:mb-8">
        <span
          class="font-body text-xs uppercase tracking-[0.3em] text-orange-700 dark:text-orange-300"
        >
          {{ t("amenities.aquapark.eyebrow") }}
        </span>
        <h2
          class="mt-3 max-w-5xl font-display text-3xl italic leading-tight text-slate-900 md:text-5xl dark:text-white"
        >
          {{ block.header }}
        </h2>
        <div class="mt-3 h-1 w-16 rounded-full bg-amber-400" />
        <p
          v-if="block.compare_intro"
          class="mt-3 max-w-3xl font-body text-base leading-relaxed text-slate-600 md:mt-4 md:text-lg dark:text-slate-300"
        >
          {{ block.compare_intro }}
        </p>
      </header>

      <article
        class="group relative overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm transition-all duration-300 hover:shadow-xl dark:border-slate-700 dark:bg-slate-800/60"
      >
        <div class="aspect-[3/2] w-full overflow-hidden md:aspect-[21/9]">
          <NuxtImg
            :src="block.image"
            :alt="block.image_alt ?? block.header"
            loading="lazy"
            width="1800"
            height="900"
            sizes="100vw md:90vw"
            class="h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.03]"
          />
        </div>
      </article>

      <div
        v-if="block.quick_facts?.length"
        class="mt-6 grid grid-cols-2 gap-3 md:mt-8 md:grid-cols-4 md:gap-4"
      >
        <div
          v-for="fact in block.quick_facts"
          :key="fact.icon + fact.label"
          class="flex items-start gap-3 rounded-2xl border border-slate-200 bg-white/90 px-4 py-4 shadow-sm backdrop-blur-sm transition-shadow hover:shadow-md md:px-5 dark:border-slate-700 dark:bg-slate-800/70"
        >
          <span
            class="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-orange-100 text-orange-700 dark:bg-orange-950/40 dark:text-orange-200"
          >
            <svg
              v-if="fact.icon === 'clock'"
              class="h-4 w-4"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              aria-hidden="true"
            >
              <circle cx="12" cy="12" r="9" />
              <path d="M12 7v5l3 2" />
            </svg>
            <svg
              v-else-if="fact.icon === 'calendar'"
              class="h-4 w-4"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              aria-hidden="true"
            >
              <rect x="3" y="5" width="18" height="16" rx="2" />
              <path d="M8 3v4M16 3v4M3 10h18" />
            </svg>
            <svg
              v-else-if="fact.icon === 'age'"
              class="h-4 w-4"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              aria-hidden="true"
            >
              <circle cx="12" cy="6" r="2.5" />
              <path d="M7 21v-5l-2-4 2-3h10l2 3-2 4v5" />
            </svg>
            <svg
              v-else-if="fact.icon === 'ticket'"
              class="h-4 w-4"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              aria-hidden="true"
            >
              <path
                d="M3 8a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v2a2 2 0 0 0 0 4v2a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-2a2 2 0 0 0 0-4Z"
              />
              <path d="M13 6v12" stroke-dasharray="2 2" />
            </svg>
          </span>
          <div class="min-w-0 flex-1 pt-0.5">
            <span
              class="block font-body text-[10px] uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400"
            >
              {{ fact.label }}
            </span>
            <span
              class="mt-1 block font-display text-base italic leading-snug text-slate-900 md:text-lg dark:text-white"
            >
              {{ fact.value }}
            </span>
          </div>
        </div>
      </div>

      <div
        v-if="block.highlights?.length"
        class="mt-8 grid grid-cols-1 gap-3 sm:grid-cols-2 md:mt-10 md:grid-cols-4 md:gap-4"
      >
        <div
          v-for="hi in block.highlights"
          :key="hi.icon + hi.label"
          class="flex items-start gap-3 rounded-2xl border border-slate-200 bg-white/90 p-4 shadow-sm transition-shadow hover:shadow-md dark:border-slate-700 dark:bg-slate-800/60"
        >
          <span
            class="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-teal-100 text-teal-700 dark:bg-teal-900/40 dark:text-teal-300"
          >
            <svg
              v-if="hi.icon === 'slide'"
              class="h-4 w-4"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              aria-hidden="true"
            >
              <path d="M3 20c4 0 4-5 8-5s4 5 8 5" />
              <path d="M6 20 16 4" />
              <circle cx="17" cy="4" r="1.5" />
            </svg>
            <svg
              v-else-if="hi.icon === 'splash'"
              class="h-4 w-4"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              aria-hidden="true"
            >
              <path d="M12 3c3 4 5 7 5 10a5 5 0 1 1-10 0c0-3 2-6 5-10Z" />
              <path d="M4 7l1.5-1M20 7l-1.5-1M3 14h1.5M19.5 14H21" />
            </svg>
            <svg
              v-else-if="hi.icon === 'lifeguard'"
              class="h-4 w-4"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              aria-hidden="true"
            >
              <circle cx="12" cy="12" r="9" />
              <circle cx="12" cy="12" r="3" />
              <path d="M12 3v4M12 17v4M3 12h4M17 12h4" />
            </svg>
            <svg
              v-else-if="hi.icon === 'wristband'"
              class="h-4 w-4"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              aria-hidden="true"
            >
              <rect x="3" y="9" width="18" height="6" rx="3" />
              <path d="M9 9v6M15 9v6" />
              <circle cx="12" cy="12" r="1.2" fill="currentColor" />
            </svg>
          </span>
          <div class="min-w-0 flex-1">
            <span
              class="block font-display text-sm italic text-slate-900 md:text-base dark:text-white"
            >
              {{ hi.label }}
            </span>
            <span
              v-if="hi.note"
              class="block font-body text-xs text-slate-500 dark:text-slate-400"
            >
              {{ hi.note }}
            </span>
          </div>
        </div>
      </div>

      <section class="mt-10 md:mt-14">
        <div
          class="overflow-hidden rounded-2xl border-l-4 border-amber-400 bg-white shadow-sm ring-1 ring-slate-200/80 dark:bg-slate-800/50 dark:ring-slate-700"
        >
          <div
            class="grid grid-cols-[1.4fr_1fr_1fr] border-b border-slate-200 bg-gradient-to-r from-orange-50/70 via-white to-orange-50/70 dark:border-slate-700 dark:from-orange-950/20 dark:via-slate-800 dark:to-orange-950/20"
          >
            <span
              class="px-3 py-2.5 font-body text-[10px] uppercase tracking-[0.15em] text-slate-500 sm:px-4 sm:py-3 sm:text-[11px] sm:tracking-[0.22em] md:px-6 md:py-4 dark:text-slate-400"
            >
              &nbsp;
            </span>
            <span
              class="px-2 py-2.5 text-center font-body text-[10px] font-semibold uppercase leading-tight tracking-[0.1em] text-slate-600 sm:px-4 sm:py-3 sm:text-[11px] sm:tracking-[0.2em] md:px-6 md:py-4 md:tracking-[0.22em] dark:text-slate-300"
            >
              {{ block.price_low_label }}
            </span>
            <span
              class="px-2 py-2.5 text-center font-body text-[10px] font-semibold uppercase leading-tight tracking-[0.1em] text-orange-700 sm:px-4 sm:py-3 sm:text-[11px] sm:tracking-[0.2em] md:px-6 md:py-4 md:tracking-[0.22em] dark:text-orange-300"
            >
              {{ block.price_high_label }}
            </span>
          </div>

          <div class="divide-y divide-slate-200 dark:divide-slate-700">
            <div
              v-for="row in block.price_rows"
              :key="row.label"
              :class="[
                'grid grid-cols-[1.4fr_1fr_1fr] items-center transition-colors',
                row.highlight
                  ? 'bg-orange-50/70 dark:bg-orange-950/20'
                  : 'bg-white dark:bg-slate-800/50',
              ]"
            >
              <span
                class="px-3 py-2.5 font-body text-[13px] leading-tight text-slate-700 sm:px-4 sm:py-3 sm:text-sm md:px-6 md:py-4 md:text-base dark:text-slate-200"
              >
                {{ row.label }}
              </span>
              <span
                class="px-2 py-2.5 text-center font-display text-base tabular-nums text-slate-700 sm:px-4 sm:py-3 sm:text-lg md:px-6 md:py-4 md:text-xl dark:text-slate-200"
              >
                <span
                  v-if="row.low_standard"
                  class="mr-1 text-xs text-slate-400 line-through sm:mr-1.5 sm:text-sm dark:text-slate-500"
                >{{ row.low_standard }}</span>
                {{ row.low_season }}
              </span>
              <span
                :class="[
                  'px-2 py-2.5 text-center font-display text-base tabular-nums sm:px-4 sm:py-3 sm:text-lg md:px-6 md:py-4 md:text-xl',
                  row.highlight
                    ? 'font-semibold text-orange-800 dark:text-orange-300'
                    : 'text-slate-700 dark:text-slate-200',
                ]"
              >
                <span
                  v-if="row.high_standard"
                  class="mr-1 text-xs font-normal text-slate-400 line-through sm:mr-1.5 sm:text-sm dark:text-slate-500"
                >{{ row.high_standard }}</span>
                {{ row.high_season }}
              </span>
            </div>
          </div>
        </div>
      </section>

      <div
        v-if="block.price_rows?.length"
        class="mt-5 flex flex-col items-start gap-3 border-t border-dashed border-slate-200 pt-4 sm:flex-row sm:items-center sm:justify-between sm:gap-5 dark:border-slate-700"
      >
        <p
          v-if="block.note"
          class="inline-flex items-start gap-2 font-body text-sm text-slate-600 dark:text-slate-300"
        >
          <svg
            class="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-500"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            aria-hidden="true"
          >
            <circle cx="12" cy="12" r="10" />
            <path d="M12 16v-4M12 8h.01" />
          </svg>
          <span>{{ block.note }}</span>
        </p>
        <NuxtLink
          :to="localePath('/search')"
          class="inline-flex flex-shrink-0 items-center gap-1.5 rounded-full bg-red-500 px-5 py-2 font-body text-sm font-semibold uppercase tracking-[0.15em] text-white shadow-md shadow-red-500/25 transition-all duration-300 hover:bg-red-600 hover:shadow-red-500/40"
        >
          {{ t('apartmentRow.cta.book') }}
          <svg
            class="h-3.5 w-3.5"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            aria-hidden="true"
          >
            <path d="M5 12h14M13 6l6 6-6 6" />
          </svg>
        </NuxtLink>
      </div>
    </div>
  </section>
</template>
